import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Intercepteur pour ajouter le token JWT à chaque requête
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const sites = {
  getAll: () => api.get('/sites'),
  getOne: (id) => api.get(`/sites/${id}`),
  create: (formData) => api.post('/sites', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/sites/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/sites/${id}`),
};

export const riskAnalyses = {
  getBySite: (siteId) => api.get(`/risk-analyses/site/${siteId}`),
  create: (data) => api.post('/risk-analyses', data),
  update: (id, data) => api.put(`/risk-analyses/${id}`, data),
  delete: (id) => api.delete(`/risk-analyses/${id}`),
  validate: (id, data) => api.put(`/risk-analyses/${id}/validate`, data),
  getAttachments: (id) => api.get(`/risk-analyses/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/risk-analyses/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (riskId, attachId) => {
    return axios.get(`${API_URL}/api/risk-analyses/${riskId}/attachments/${attachId}/download`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  deleteAttachment: (riskId, attachId) => api.delete(`/risk-analyses/${riskId}/attachments/${attachId}`),
};

export const operatingBudgets = {
  getBySite: (siteId) => api.get(`/operating-budgets/site/${siteId}`),
  bulkUpdate: (siteId, budgets) => api.post('/operating-budgets/bulk', { site_id: siteId, budgets }),
  update: (id, data) => api.put(`/operating-budgets/${id}`, data),
};

export const investmentBudgets = {
  getBySite: (siteId) => api.get(`/investment-budgets/site/${siteId}`),
  bulkUpdate: (siteId, budgets) => api.post('/investment-budgets/bulk', { site_id: siteId, budgets }),
  update: (id, data) => api.put(`/investment-budgets/${id}`, data),
};

export const batiments = {
  getBySite: (siteId) => api.get(`/batiments/site/${siteId}`),
  bulkUpdate: (siteId, batiments) => api.post(`/batiments/site/${siteId}`, { batiments }),
  delete: (id) => api.delete(`/batiments/${id}`),
};

export const assessments = {
  getBySite: (siteId) => api.get(`/assessments/site/${siteId}`),
  getOne: (id) => api.get(`/assessments/${id}`),
  create: (data) => api.post('/assessments', data),
  saveDraft: (id, answers) => api.put(`/assessments/${id}`, { answers }),
  complete: (id, answers, lang) => api.post(`/assessments/${id}/complete`, { answers, lang }),
  accept: (id, selectedKeys) => api.post(`/assessments/${id}/accept`, { selectedKeys }),
  getInvestments: (id) => api.get(`/assessments/${id}/investments`),
  getExport: (id) => api.get(`/assessments/${id}/export`),
  getLatestForExport: (siteIds) => api.post('/assessments/export/latest', { siteIds }),
  reset: (id) => api.post(`/assessments/${id}/reset`),
  delete: (id) => api.delete(`/assessments/${id}`),
};

export const siteDocuments = {
  getBySite: (siteId) => api.get(`/sites/${siteId}/documents`),
  upload: (siteId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/sites/${siteId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (siteId, docId) => {
    return axios.get(`${API_URL}/api/sites/${siteId}/documents/${docId}/download`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  delete: (siteId, docId) => api.delete(`/sites/${siteId}/documents/${docId}`),
};

export const pdf = {
  generateSitePdf: (siteId, lang = 'fr') => {
    return axios.get(`${API_URL}/api/pdf/site/${siteId}?lang=${lang}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
  }
};

export const admin = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getPermissions: () => api.get('/admin/permissions'),
  savePermission: (data) => api.post('/admin/permissions', data),
  deletePermission: (id) => api.delete(`/admin/permissions/${id}`),
  getPendingValidations: () => api.get('/admin/pending-validations'),
};

export default api;
